import {
  ChevronDown,
  Download,
  ExternalLink,
  FileCode,
  FileText,
  Pencil,
  StickyNote,
  Trash2,
  Upload,
} from "lucide-react";
import { useState } from "react";
import { createPortal } from "react-dom";
import type { CollectionFile } from "./utils";
import { formatFileSize, getSourceTypeLabel, getStatusConfig, hasMarkdownVersion } from "./utils";

export interface DataSourceFileTableProps {
  files: CollectionFile[];
  sourceType: string | null | undefined;
  onDownload: (file: CollectionFile) => void;
  onDownloadMarkdown: (file: CollectionFile) => void;
  onEditNote: (file: CollectionFile) => void;
  onDelete: (file: CollectionFile) => void;
  onUpload: () => void;
  onAddNote: () => void;
}

export function DataSourceFileTable({
  files,
  sourceType: _sourceType,
  onDownload,
  onDownloadMarkdown,
  onEditNote,
  onDelete,
  onUpload,
  onAddNote,
}: Readonly<DataSourceFileTableProps>) {
  const [downloadMenuOpen, setDownloadMenuOpen] = useState<string | null>(null);
  const [downloadMenuPos, setDownloadMenuPos] = useState<{ top: number; right: number } | null>(null);

  if (files.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <div className="h-16 w-16 rounded-2xl bg-linear-to-br from-mutual-orange/20 to-mutual-purple/20 flex items-center justify-center mb-4">
          <FileText className="h-8 w-8 text-brand-primary" />
        </div>
        <h3 className="text-lg font-medium text-content-primary">No files yet</h3>
        <p className="text-content-tertiary mt-1 text-center max-w-sm">
          Add files, paste notes, or sync from an external source to populate this knowledge base.
        </p>
        <div className="mt-6 flex items-center gap-3">
          <button
            onClick={onUpload}
            className="flex items-center gap-2 px-4 py-2.5 bg-brand-gradient text-white rounded-lg font-medium hover:opacity-90 transition-opacity"
          >
            <Upload className="h-4 w-4" />
            Upload Files
          </button>
          <button
            onClick={onAddNote}
            className="flex items-center gap-2 px-4 py-2.5 bg-surface-secondary hover:bg-interactive-active text-content-primary rounded-lg font-medium transition-colors"
          >
            <StickyNote className="h-4 w-4" />
            Add Note
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="bg-surface-primary border border-border-primary rounded-lg overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border-primary bg-surface-primary/50">
              <th className="text-left px-6 py-3 text-xs font-medium text-content-tertiary uppercase tracking-wider">
                Filename
              </th>
              <th className="text-left px-6 py-3 text-xs font-medium text-content-tertiary uppercase tracking-wider">
                Size
              </th>
              <th className="text-left px-6 py-3 text-xs font-medium text-content-tertiary uppercase tracking-wider">
                Type
              </th>
              <th className="text-left px-6 py-3 text-xs font-medium text-content-tertiary uppercase tracking-wider">
                Source
              </th>
              <th className="text-left px-6 py-3 text-xs font-medium text-content-tertiary uppercase tracking-wider">
                Status
              </th>
              <th className="text-left px-6 py-3 text-xs font-medium text-content-tertiary uppercase tracking-wider">
                Added
              </th>
              <th className="text-right px-6 py-3 text-xs font-medium text-content-tertiary uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border-primary">
            {files.map((file) => {
              const statusConfig = getStatusConfig(file.status, file.errorMessage);
              const StatusIcon = statusConfig.icon;

              return (
                <tr key={file.uuid} className="hover:bg-interactive-hover transition-colors group">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <FileText className="h-5 w-5 text-content-quaternary shrink-0" />
                      {file.sourceType === "website" && file.sourceUrl ? (
                        <a
                          href={file.sourceUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-brand-primary hover:underline truncate max-w-md flex items-center gap-1"
                        >
                          {file.sourceUrl}
                          <ExternalLink className="h-3 w-3 shrink-0" />
                        </a>
                      ) : (
                        <span className="text-content-primary truncate max-w-md">{file.filename}</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-content-tertiary text-sm">{formatFileSize(file.fileSize)}</td>
                  <td className="px-6 py-4 text-content-tertiary text-sm">{file.mimeType || "Unknown"}</td>
                  <td className="px-6 py-4 text-content-tertiary text-sm">
                    {getSourceTypeLabel(file.sourceType) || <span className="text-content-quaternary">-</span>}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-col gap-1">
                      <div
                        className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-sm ${statusConfig.bg} w-fit`}
                        title={statusConfig.tooltip}
                      >
                        <StatusIcon
                          className={`h-3 w-3 ${statusConfig.color} ${statusConfig.animate ? "animate-spin" : ""}`}
                        />
                        <span className={`text-xs font-medium ${statusConfig.color}`}>{statusConfig.label}</span>
                      </div>
                      {statusConfig.tooltip && (
                        <span className="text-xs text-content-quaternary max-w-sm" title={statusConfig.tooltip}>
                          {statusConfig.tooltip}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-content-tertiary text-sm">
                    {new Date(file.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-end gap-1">
                      {hasMarkdownVersion(file) ? (
                        <button
                          onClick={(e) => {
                            if (downloadMenuOpen === file.uuid) {
                              setDownloadMenuOpen(null);
                              setDownloadMenuPos(null);
                            } else {
                              const rect = e.currentTarget.getBoundingClientRect();
                              setDownloadMenuPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
                              setDownloadMenuOpen(file.uuid);
                            }
                          }}
                          className="flex items-center gap-0.5 p-2.5 text-content-tertiary hover:text-content-primary hover:bg-interactive-active rounded-lg transition-colors"
                          title="Download options"
                        >
                          <Download className="h-4 w-4" />
                          <ChevronDown className="h-3 w-3" />
                        </button>
                      ) : (
                        <button
                          onClick={() => onDownload(file)}
                          className="p-2.5 text-content-tertiary hover:text-content-primary hover:bg-interactive-active rounded-lg transition-colors"
                          title="Download"
                        >
                          <Download className="h-4 w-4" />
                        </button>
                      )}
                      {file.sourceType === "notes" && (
                        <button
                          onClick={() => onEditNote(file)}
                          className="p-2.5 text-content-tertiary hover:text-content-primary hover:bg-interactive-active rounded-lg transition-colors"
                          title="Edit note"
                          aria-label={`Edit ${file.filename}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                      )}
                      <button
                        onClick={() => onDelete(file)}
                        className="p-2.5 text-content-tertiary hover:text-red-400 hover:bg-interactive-active rounded-lg transition-colors"
                        title="Delete file"
                        aria-label={`Delete ${file.filename}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Download dropdown menu (portaled to body to avoid overflow clipping) */}
      {downloadMenuOpen &&
        downloadMenuPos &&
        createPortal(
          <>
            <div // NOSONAR — decorative backdrop element
              className="fixed inset-0 z-[9998]"
              role="presentation"
              onClick={() => {
                setDownloadMenuOpen(null);
                setDownloadMenuPos(null);
              }}
              onKeyDown={(e) => {
                if (e.key === "Escape") {
                  setDownloadMenuOpen(null);
                  setDownloadMenuPos(null);
                }
              }}
            />
            <div
              role="menu"
              className="fixed z-[9999] w-52 bg-surface-secondary border border-border-primary rounded-lg shadow-xl py-1"
              style={{ top: downloadMenuPos.top, right: downloadMenuPos.right }}
            >
              <button
                role="menuitem"
                onMouseDown={(e) => {
                  e.preventDefault();
                  const file = files.find((f) => f.uuid === downloadMenuOpen);
                  if (file) onDownload(file);
                  setDownloadMenuOpen(null);
                  setDownloadMenuPos(null);
                }}
                className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-content-secondary hover:bg-interactive-hover hover:text-content-primary transition-colors text-left whitespace-nowrap"
              >
                <Download className="h-3.5 w-3.5 shrink-0" />
                Original file
              </button>
              <button
                role="menuitem"
                onMouseDown={(e) => {
                  e.preventDefault();
                  const file = files.find((f) => f.uuid === downloadMenuOpen);
                  if (file) onDownloadMarkdown(file);
                  setDownloadMenuOpen(null);
                  setDownloadMenuPos(null);
                }}
                className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-content-secondary hover:bg-interactive-hover hover:text-content-primary transition-colors text-left whitespace-nowrap"
              >
                <FileCode className="h-3.5 w-3.5 shrink-0" />
                AI-ready (Markdown)
              </button>
            </div>
          </>,
          document.body,
        )}
    </>
  );
}
